import { useQuery } from '@apollo/client/react'


import {
  cartOpenStore, cartStore, watchOpenStore, useStore, relockPointer, segnalaCassa,
} from '../../store/uiStore'
import useCart, { formatPrice } from '../../hooks/useCart'
import { GET_SHOP_POLICIES } from '../Queries'
import { URL_ACCOUNT } from '../../config/negozio'
import { IS_TOUCH } from '../../config/touch'

// ============================================================
// Carrello Shopify: cassetto laterale (su telefono sale dal basso). Si
// apre dall'app "Carrello" dell'orologio o col tasto C.
// IL PAGAMENTO NON PARTE PIU' DA QUI: si va al bancone, il POS stampa
// lo scontrino e da lì si passa al checkout Shopify (vedi Cassa.jsx e
// CassaOverlay.jsx). Il negozio si attraversa a piedi, pagare da un
// pannello qualunque svuotava di senso la cassa.
// NB: col puntatore bloccato (PointerLock) i click sulla UI 2D
// non arrivano → premere C (o Esc e poi cliccare).
// ============================================================

export default function CartUI() {
  return (
    <>
      <CartButton />
      <CartDrawer />
    </>
  )
}

// Il pulsante del carrello ora è un'icona disegnata sul quadrante
// dell'orologio (OrologioFaccia). Questo componente resta perché è lui
// a inizializzare il carrello dal localStorage al primo montaggio.
function CartButton() {
  useCart()
  return null
}

function CartDrawer() {
  const open = useStore(cartOpenStore)
  const { cart, updateLine, removeLine, updating } = useCart()

  // Link alle politiche del negozio (obbligatorie per e-commerce UE):
  // compaiono in fondo al drawer man mano che vengono configurate
  const policiesQuery = useQuery(GET_SHOP_POLICIES)
  const shopPolicies = policiesQuery.data?.shop
  // NOMI IN ITALIANO: il titolo che manda Shopify e' quello del pannello,
  // che qui era rimasto in inglese ("Refund Policy", "Terms of Service") in
  // un negozio tutto italiano. Si sa quale politica e' quale, quindi il nome
  // lo si decide qui.
  const policyLinks = [
    [shopPolicies?.shippingPolicy, 'Spedizioni'],
    [shopPolicies?.refundPolicy, 'Resi e rimborsi'],
    [shopPolicies?.termsOfService, 'Termini di servizio'],
    [shopPolicies?.privacyPolicy, 'Privacy'],
  ].filter(([pol]) => pol).map(([pol, nome]) => ({ url: pol.url, nome }))

  if (!open) return null

  const lines = cart?.lines?.edges?.map((e) => e.node) || []

  // LO SCONTO SI VEDE ANCHE QUI. Prima il codice diceva "applicato" e sotto
  // c'era il subtotale pieno: lo sconto compariva solo sullo scontrino. Gli
  // importi sono quelli CALCOLATI da Shopify, sul carrello e sulle righe,
  // come in Cassa.jsx.
  const sconto = [
    ...(cart?.discountAllocations || []),
    ...lines.flatMap((l) => l.discountAllocations || []),
  ].reduce((tot, a) => tot + Number(a.discountedAmount?.amount || 0), 0)
  const valuta = cart?.cost?.subtotalAmount?.currencyCode || 'EUR'
  const close = () => {
    cartOpenStore.set(false)
    relockPointer() // click su ✕/backdrop → mouse subito riagganciato al canvas
  }

  return (
    <div className="cart-backdrop" onClick={close}>
      <aside className="cart-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="cart-header">
          <h2>Carrello</h2>
          <button className="pp-close" onClick={close} aria-label="Chiudi">✕</button>
        </div>

        {lines.length === 0 && (
          <p className="cart-empty">
            Il carrello è vuoto.<br />
            {IS_TOUCH
              ? <>Esplora il negozio e tocca <b>Guarda</b> davanti a un prodotto!</>
              : <>Esplora il negozio e premi <kbd>E</kbd> davanti a un prodotto!</>}
          </p>
        )}

        <div className="cart-lines">
          {lines.map((line) => {
            const v = line.merchandise
            const realOptions = v.selectedOptions.filter((o) => o.value !== 'Default Title')
            return (
              <div className="cart-line" key={line.id}>
                {v.image?.url && <img src={v.image.url} alt={v.product.title} />}
                <div className="cart-line-info">
                  <div className="cart-line-title">{v.product.title}</div>
                  {realOptions.length > 0 && (
                    <div className="cart-line-options">
                      {realOptions.map((o) => `${o.name}: ${o.value}`).join(' · ')}
                    </div>
                  )}
                  <div className="pp-qty small">
                    <button
                      disabled={updating}
                      onClick={() =>
                        line.quantity <= 1
                          ? removeLine(line.id)
                          : updateLine(line.id, line.quantity - 1)
                      }
                    >−</button>
                    <span>{line.quantity}</span>
                    <button disabled={updating} onClick={() => updateLine(line.id, line.quantity + 1)}>+</button>
                  </div>
                </div>
                <div className="cart-line-right">
                  <div className="cart-line-price">{formatPrice(line.cost.totalAmount)}</div>
                  <button className="cart-line-remove" disabled={updating} onClick={() => removeLine(line.id)}>
                    Rimuovi
                  </button>
                </div>
              </div>
            )
          })}
        </div>

        {/* I premi della macchinetta: il codice e' gia' attaccato al
            carrello e Shopify lo applica al pagamento (la spedizione gratis
            si vede solo li', quando c'e' un indirizzo) */}
        {cart?.discountCodes?.length > 0 && (
          <div className="cart-codici">
            {cart.discountCodes.map((d) => (
              <div key={d.code} className="cart-codice">
                <span className="cart-codice-nome">{d.code}</span>
                <span>{d.applicable ? 'applicato' : 'si applica al pagamento'}</span>
              </div>
            ))}
          </div>
        )}

        {lines.length > 0 && (
          <div className="cart-footer">
            <div className="cart-subtotal">
              <span>Subtotale</span>
              <strong>{formatPrice(cart.cost.subtotalAmount)}</strong>
            </div>
            {sconto > 0 && (
              <>
                <div className="cart-riga-sconto">
                  <span>Sconti</span>
                  <span>−{formatPrice({ amount: sconto, currencyCode: valuta })}</span>
                </div>
                <div className="cart-subtotal cart-totale">
                  <span>Totale</span>
                  <strong>{formatPrice(cart.cost.totalAmount)}</strong>
                </div>
              </>
            )}
            {/* "Vai in cassa" non paga: manda AL BANCONE. Quindi illumina il
                POS (segnalaCassa) e ABBASSA IL POLSO — il carrello si apre
                anche dall'orologio, e restare col braccio alzato mentre si
                cammina verso la cassa non ha senso (chiesto dall'utente). */}
            <button
              className="pp-add-btn"
              onClick={() => {
                cartOpenStore.set(false)
                watchOpenStore.set(false)
                segnalaCassa()
                relockPointer()
              }}
            >
              Vai in cassa
            </button>
            <p className="cart-note">
              {IS_TOUCH
                ? 'Il pagamento si fa al bancone: avvicinati al POS e tocca “Paga”.'
                : 'Il pagamento si fa al bancone: avvicinati al POS e premi E.'}
            </p>
          </div>
        )}

        {/* una riga sola: l'account (di Shopify: ordini e indirizzi) e le
            politiche. Erano due righe separate da un filetto. */}
        <div className="cart-policies">
          <a href={URL_ACCOUNT} target="_blank" rel="noreferrer">Il mio account</a>
          {policyLinks.map((p) => (
            <a key={p.url} href={p.url} target="_blank" rel="noreferrer">{p.nome}</a>
          ))}
        </div>
      </aside>
    </div>
  )
}
